import React, { useState } from 'react';
import { Text, View } from 'react-native';
import { CalcShell, FormulaLine, FormulaNote, NumericField } from '../../../components/shell';
import { DiagramMixedAirPsych } from '../../../components/diagrams';
import { usePersistedState } from '../../../hooks/usePersistedState';
import { useUnits } from '../../../context/UnitsContext';
import { mixedAirFromStreams } from '../../../lib/calcs/psychrometrics';
import { C } from '../../../theme/tokens';

const KEY = 'tabai.calc.psychroMixedAir.v1';

type Fields = { cfm1: string; t1: string; rh1: string; cfm2: string; t2: string; rh2: string };
const EMPTY: Fields = { cfm1: '1000', t1: '95', rh1: '40', cfm2: '4000', t2: '75', rh2: '50' };

export default function PsychrometricsMixedAirScreen() {
  const { unit, toImp, fromImp } = useUnits();
  const [fields, setFields, { clear: clearPersisted }] = usePersistedState<Fields>(KEY, EMPTY);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<{ tDryBulbF: number; rhPercent: number; humidityRatio: number; enthalpyBtuLb: number } | null>(null);

  const set = (patch: Partial<Fields>) => setFields((prev) => ({ ...prev, ...patch }));

  const calculate = () => {
    setError(null);
    // All computations inside mixedAirFromStreams are Imperial — convert inputs first.
    const C1 = toImp(fields.cfm1, 'flow');
    const T1 = toImp(fields.t1, 'temperature');
    const RH1 = Number(fields.rh1);
    const C2 = toImp(fields.cfm2, 'flow');
    const T2 = toImp(fields.t2, 'temperature');
    const RH2 = Number(fields.rh2);

    if (!Number.isFinite(C1) || C1 < 0) { setError('Stream 1 flow must be ≥ 0'); return; }
    if (!Number.isFinite(C2) || C2 < 0) { setError('Stream 2 flow must be ≥ 0'); return; }
    if (!Number.isFinite(T1) || !Number.isFinite(T2)) { setError('Temperatures must be numbers'); return; }
    if (!Number.isFinite(RH1) || RH1 < 0 || RH1 > 100) { setError('Stream 1 RH must be 0–100'); return; }
    if (!Number.isFinite(RH2) || RH2 < 0 || RH2 > 100) { setError('Stream 2 RH must be 0–100'); return; }

    try {
      const r = mixedAirFromStreams(
        { cfm: C1, tDryBulbF: T1, rhPercent: RH1 },
        { cfm: C2, tDryBulbF: T2, rhPercent: RH2 },
      );
      setResult(r);
    } catch {
      setError('Unable to calculate. Check inputs.');
    }
  };

  const clearAll = () => {
    setError(null);
    void clearPersisted();
    setResult(null);
  };

  const resultLines = result
    ? [
        { label: 'Dry-bulb', value: `${fromImp(result.tDryBulbF, 'temperature', 1)} ${unit('temperature')}` },
        { label: 'RH', value: `${result.rhPercent.toFixed(1)} %` },
        { label: 'Humidity Ratio (W)', value: `${fromImp(result.humidityRatio, 'humidityRatio', 5)} ${unit('humidityRatio')}` },
        { label: 'Enthalpy', value: `${fromImp(result.enthalpyBtuLb, 'enthalpy', 1)} ${unit('enthalpy')}`, emphasis: true as const },
      ]
    : null;

  return (
    <CalcShell
      title="Mixed Air (Psychrometrics)"
      subtitle="Mix two airstreams by flow using psychrometric properties"
      formula={
        <>
          <FormulaLine>Wm = (Q1·W1 + Q2·W2) / (Q1 + Q2)</FormulaLine>
          <FormulaLine>hm = (Q1·h1 + Q2·h2) / (Q1 + Q2)</FormulaLine>
          <FormulaNote>Approximate mass balance — field TAB use (flow proportional)</FormulaNote>
        </>
      }
      error={error}
      onCalculate={calculate}
      onClear={clearAll}
      result={resultLines}
      copyFooter="Formula: Mixed air mass balance (flow weighted)"
      diagram={<DiagramMixedAirPsych />}
      outputSlots={{
        outside_air_temp_f: fields.t1 ? toImp(fields.t1, 'temperature') : undefined,
        return_air_temp_f: fields.t2 ? toImp(fields.t2, 'temperature') : undefined,
        mixed_air_temp_f: result ? result.tDryBulbF : undefined,
        enthalpy_btu_lb: result ? result.enthalpyBtuLb : undefined,
        humidity_ratio_lb_lb: result ? result.humidityRatio : undefined,
        rh_pct: result ? result.rhPercent : undefined,
      }}
      onPrefill={(values) => {
        const patch: Partial<Fields> = {};
        if (values.outside_air_temp_f != null && !fields.t1) {
          patch.t1 = fromImp(values.outside_air_temp_f, 'temperature', 1);
        }
        if (values.return_air_temp_f != null && !fields.t2) {
          patch.t2 = fromImp(values.return_air_temp_f, 'temperature', 1);
        }
        if (values.rh_pct != null && !fields.rh1) {
          patch.rh1 = values.rh_pct.toFixed(1);
        }
        if (Object.keys(patch).length > 0) set(patch);
      }}
    >
      <Text style={{ fontSize: 16, fontWeight: '700', marginBottom: 8, color: C.ink }}>Stream 1</Text>
      <View style={{ marginBottom: 12 }}>
        <NumericField label="Airflow" value={fields.cfm1} onChangeText={(v) => set({ cfm1: v })} rightText={unit('flow')} maxDecimals={1} onSubmitEditing={calculate} />
      </View>
      <View style={{ marginBottom: 12 }}>
        <NumericField label="Dry-bulb" value={fields.t1} onChangeText={(v) => set({ t1: v })} rightText={unit('temperature')} maxDecimals={1} onSubmitEditing={calculate} />
      </View>
      <View style={{ marginBottom: 12 }}>
        <NumericField label="RH" value={fields.rh1} onChangeText={(v) => set({ rh1: v })} rightText="%" maxDecimals={1} onSubmitEditing={calculate} />
      </View>
      <Text style={{ fontSize: 16, fontWeight: '700', marginBottom: 8, color: C.ink }}>Stream 2</Text>
      <View style={{ marginBottom: 12 }}>
        <NumericField label="Airflow" value={fields.cfm2} onChangeText={(v) => set({ cfm2: v })} rightText={unit('flow')} maxDecimals={1} onSubmitEditing={calculate} />
      </View>
      <View style={{ marginBottom: 12 }}>
        <NumericField label="Dry-bulb" value={fields.t2} onChangeText={(v) => set({ t2: v })} rightText={unit('temperature')} maxDecimals={1} onSubmitEditing={calculate} />
      </View>
      <View style={{ marginBottom: 12 }}>
        <NumericField label="RH" value={fields.rh2} onChangeText={(v) => set({ rh2: v })} rightText="%" maxDecimals={1} onSubmitEditing={calculate} />
      </View>
    </CalcShell>
  );
}
